import { Component, DestroyRef, computed, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import {
  BalanceService,
  config,
  ScorecardActionType,
  ScorecardFacade,
  ScorecardV2Action,
  VoucherRedeemModel,
  VoucherService,
} from '@fgb/core';
import { take } from 'rxjs/operators';

/**
 * How an incomplete action row behaves.
 * 'expand' rows open a panel under the header (e.g. Enter Code) instead of linking away.
 */
export type ScorecardV2ActionRowType = 'plain' | 'link' | 'expand';

/** The panels an expandable action row can open. Add a new panel here and in the template's panel switch. */
export type ScorecardV2ActionPanel = 'voucher';

/** IDs not in the V1 ScorecardActionType enum yet */
const PICK_A_PLAYER_ACTION_ID = 200060;
const PAYMENT_ACTION_ID = 200070;
const LOTTO_ENTRY_ACTION_ID = 200024;
const REFER_A_FRIEND_ACTION_ID = 200014;
const VOUCHER_CODE_ACTION_ID = 220000;

/** How long a voucher result message stays, matching the regular voucher toast delay */
const VOUCHER_MESSAGE_DELAY = 10000;

/**
 * Pages an action links to, keyed by LoyaltyActionId. An incomplete action with an entry here shows the arrow.
 */
export const SCORECARD_V2_ACTION_ROUTES: Readonly<Record<number, string>> = {
  [ScorecardActionType.marketplace]: '/rewards/marketplace',
  [ScorecardActionType.auction]: '/rewards/auctions',
  [ScorecardActionType.lotto]: '/rewards/raffles',
  [LOTTO_ENTRY_ACTION_ID]: '/rewards/raffles',
  [ScorecardActionType.polls]: '/games/poll',
  [ScorecardActionType.trivia]: '/games/trivia',
  [ScorecardActionType.triviaGroup]: '/games/trivia',
  [ScorecardActionType.predictor]: '/games/predictor',
  [PICK_A_PLAYER_ACTION_ID]: '/games/pickaplayer',
  [PAYMENT_ACTION_ID]: '/payment',
  [REFER_A_FRIEND_ACTION_ID]: '/referafriend',
};

/**
 * Actions completed from a panel inside the row, keyed by LoyaltyActionId. Checked before the routes,
 * so an incomplete action with an entry here shows the +/- toggle instead of the arrow.
 */
export const SCORECARD_V2_ACTION_PANELS: Readonly<Record<number, ScorecardV2ActionPanel>> = {
  [VOUCHER_CODE_ACTION_ID]: 'voucher',
};

/** A voucher result message, using the same types as the regular voucher component's toasts */
export interface ScorecardV2VoucherMessage {
  type: 'success' | 'fail' | 'notvalid';
  key: string;
}

let nextActionRowId = 0;

/** One action row in the Scorecards V2 description panel. */
@Component({
  selector: 'fgbcl-scorecards-v2-action',
  templateUrl: './scorecards-v2-action.component.html',
  styleUrl: './scorecards-v2-action.component.scss',
})
export class ScorecardsV2ActionComponent {
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private voucherService = inject(VoucherService);
  private balanceService = inject(BalanceService);
  private scorecardFacade = inject(ScorecardFacade);

  action = input.required<ScorecardV2Action>();
  /** Emits when a linked action is followed, so the panel can close */
  linkFollowed = output<void>();

  readonly name = computed(() => this.action().Content?.[0]?.Name ?? '');
  readonly route = computed<string | undefined>(() => SCORECARD_V2_ACTION_ROUTES[this.action().LoyaltyActionId]);
  readonly panel = computed<ScorecardV2ActionPanel | undefined>(
    () => SCORECARD_V2_ACTION_PANELS[this.action().LoyaltyActionId]
  );
  readonly rowType = computed<ScorecardV2ActionRowType>(() => {
    if (this.panel()) return 'expand';
    return this.route() ? 'link' : 'plain';
  });

  /** Unique ids so each row's toggle, panel and input can reference each other */
  private readonly rowId = nextActionRowId++;
  readonly panelId = `fgbcl-sc-action-panel-${this.rowId}`;
  readonly voucherInputId = `fgbcl-sc-action-voucher-${this.rowId}`;

  /** Open state of this row's panel */
  readonly expanded = signal(false);

  readonly voucherCode = signal('');
  readonly voucherSubmitting = signal(false);
  readonly voucherMessage = signal<ScorecardV2VoucherMessage | null>(null);
  private voucherMessageTimeout?: ReturnType<typeof setTimeout>;

  constructor() {
    this.destroyRef.onDestroy(() => clearTimeout(this.voucherMessageTimeout));
  }

  followLink(): void {
    const route = this.route();
    if (!route) return;
    this.linkFollowed.emit();
    this.router.navigateByUrl(route);
  }

  toggle(): void {
    this.expanded.update((open) => !open);
  }

  onVoucherInput(event: Event): void {
    this.voucherCode.set((event.target as HTMLInputElement).value);
  }

  /** Redeems the code the same way as the regular voucher component, then refreshes the scorecards */
  redeemVoucher(event?: Event): void {
    event?.preventDefault();
    if (this.voucherSubmitting()) return;

    const code = this.voucherCode().trim();
    if (!code) {
      this.showVoucherMessage('notvalid', 'voucher.code.not.valid');
      return;
    }

    const voucher = new VoucherRedeemModel();
    voucher.VoucherCode = code;

    this.voucherSubmitting.set(true);
    this.voucherService
      .redeemVoucher(voucher)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.voucherSubmitting.set(false);
          this.voucherCode.set('');
          this.showVoucherMessage('success', 'voucher.code.success');
          this.balanceService.fetchBalance(config.purseConfig.virtualLoyalty).pipe(take(1)).subscribe();
          // the store caches for 60s, so clear it to show the action as done and the new progress
          this.scorecardFacade.clearCache();
          this.scorecardFacade.fetchScorecards();
        },
        error: () => {
          this.voucherSubmitting.set(false);
          this.showVoucherMessage('fail', 'voucher.code.error');
        },
      });
  }

  private showVoucherMessage(type: ScorecardV2VoucherMessage['type'], key: string): void {
    clearTimeout(this.voucherMessageTimeout);
    this.voucherMessage.set({ type, key });
    this.voucherMessageTimeout = setTimeout(() => this.voucherMessage.set(null), VOUCHER_MESSAGE_DELAY);
  }
}
